var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Ball } from './Ball';
import { BallScene } from './BallScene';
import RAPIER from '@dimforge/rapier3d-compat';
import { ACESFilmicToneMapping, Clock, DirectionalLight, MeshStandardMaterial, Object3D, PCFSoftShadowMap, PerspectiveCamera, PMREMGenerator, Scene, Vector2, WebGLRenderer } from 'three';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
export var AuthSceneEvents;
(function (AuthSceneEvents) {
    AuthSceneEvents["throwWhiteBall"] = "throwWhiteBall";
    AuthSceneEvents["throwGreenBall"] = "throwGreenBall";
})(AuthSceneEvents || (AuthSceneEvents = {}));
export class AuthSceneLogic {
    constructor(width, height, canvas) {
        this.balls = [];
        this.animatedBalls = [];
        this.receiveEvents = true;
        this.disposed = false;
        this.clock = new Clock();
        this.throwWhiteBall = this.throwWhiteBall.bind(this);
        this.throwGreenBall = this.throwGreenBall.bind(this);
        this.removeBall = this.removeBall.bind(this);
        this.ballScene = new BallScene();
        const gravity = new RAPIER.Vector3(0.0, -6.5, 0.0);
        this.world = new RAPIER.World(gravity);
        this.dynamicBodies = [];
        this.scene = new Scene();
        const light1 = new DirectionalLight(undefined, 1.2);
        light1.position.set(5, 11, 5);
        light1.castShadow = true;
        light1.shadow.mapSize = new Vector2(256, 256);
        light1.shadow.camera.far = 12;
        light1.shadow.blurSamples = 25;
        light1.shadow.radius = 25;
        const targetObject = new Object3D();
        targetObject.position.set(0, 8, 0);
        this.scene.add(targetObject);
        light1.target = targetObject;
        this.scene.add(light1);
        const light2 = light1.clone();
        light2.position.set(-5, 11, 5);
        light2.target = targetObject;
        this.scene.add(light2);
        this.camera = new PerspectiveCamera(35, (0.5 * width) / height, 0.1, 100);
        this.camera.position.set(-1.8, 7.5, 6.7);
        this.renderer = new WebGLRenderer({ antialias: true, canvas });
        this.renderer.setSize(0.5 * width, height);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = PCFSoftShadowMap;
        this.renderer.toneMapping = ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1;
    }
    static initRapier() {
        return __awaiter(this, void 0, void 0, function* () {
            yield RAPIER.init();
        });
    }
    loadBallScene() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new EXRLoader().load('assets/env/studio_small_08_1k.exr', (texture) => {
                const pMREMGenerator = new PMREMGenerator(this.renderer);
                pMREMGenerator.compileEquirectangularShader();
                const prTexture = pMREMGenerator.fromEquirectangular(texture).texture;
                this.scene.environment = prTexture;
                this.scene.background = prTexture;
            });
            this.bs = new BallScene();
            yield this.bs.loadBall();
            yield this.bs.loadScene(this.scene, [0, 0, 0], this.world);
        });
    }
    updateCameraOnWindowResize(width, height) {
        this.camera.aspect = (0.5 * width) / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(0.5 * width, height);
    }
    throwWhiteBall() {
        if (this.receiveEvents) {
            this.createBall(0xffffff);
        }
    }
    throwGreenBall() {
        if (this.receiveEvents) {
            this.createBall(0x7ba158);
        }
    }
    createBall(ballColor) {
        const sphereMesh = new Ball(this.bs.ball.geometry, new MeshStandardMaterial({
            roughness: 0.3,
            color: ballColor
        }));
        this.scene.add(sphereMesh);
        this.balls.push(sphereMesh);
        const sphereBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(Math.random() * -3, 10, 0.2)
            .setCanSleep(false));
        const sphereShape = RAPIER.ColliderDesc.ball(0.2).setMass(1).setRestitution(0.5);
        this.world.createCollider(sphereShape, sphereBody);
        this.dynamicBodies.push([sphereMesh, sphereBody]);
    }
    removeBall(event) {
        if (event.key === 'Backspace') {
            if (this.balls.length > 0) {
                const bl = this.balls.pop();
                bl.isAnimationStarted = true;
                this.animatedBalls.push(bl);
                this.receiveEvents = false;
            }
        }
        else {
            this.receiveEvents = true;
        }
    }
    animate() {
        if (this.disposed) {
            return;
        }
        requestAnimationFrame(this.animate.bind(this));
        const delta = this.clock.getDelta();
        this.world.timestep = Math.min(delta, 0.1);
        this.world.step();
        this.animatedBalls.forEach((ball, index) => {
            if (ball.isAnimationStarted && !ball.isAnimationFinished()) {
                ball.update(delta);
            }
            else if (ball.isAnimationFinished()) {
                this.scene.remove(ball);
                this.animatedBalls.splice(index, 1);
            }
        });
        this.balls.forEach((ball, index) => {
            if (ball.position.y > 0 && ball.position.y < 4) {
                this.scene.remove(ball);
                this.balls.splice(index, 1);
            }
        });
        for (let i = 0, n = this.dynamicBodies.length; i < n; i++) {
            this.dynamicBodies[i][0].position.copy(this.dynamicBodies[i][1].translation());
            this.dynamicBodies[i][0].quaternion.copy(this.dynamicBodies[i][1].rotation());
        }
        this.ballScene.update();
        this.renderer.render(this.scene, this.camera);
    }
    dispose() {
        this.disposed = true;
        this.renderer.dispose();
    }
}
//# sourceMappingURL=AuthSceneLogic.js.map